import { createContext, type ReactNode, useContext, useMemo } from 'react';

/**
 * Resolved content-route context.
 *
 * The slug-based content routes (`/{program}/{subSpace}/{lesson}/...`) resolve
 * their slug chain to entity ids in the route loader and expose both the ids
 * (for data fetching) and the ancestor slugs (for building links to sibling /
 * child pages) through this context. Feature components read ids/slugs from here
 * instead of `Route.useParams()`, so the same components render under the slug
 * tree regardless of how deep the current URL is.
 */
export interface ContentRouteValue {
  programId: string;
  programSlug: string;
  /** Sub-space (module or feed) — absent on the program home. */
  subSpaceId?: string;
  subSpaceSlug?: string;
  /** Lesson — absent above the lesson level. */
  lessonId?: string;
  lessonSlug?: string;
}

const ContentRouteContext = createContext<ContentRouteValue | null>(null);

export const ContentRouteProvider = ({ value, children }: { value: ContentRouteValue; children: ReactNode }) => {
  const { programId, programSlug, subSpaceId, subSpaceSlug, lessonId, lessonSlug } = value;
  // Memoize so identical resolutions don't retrigger consumers on parent renders.
  const memoized = useMemo<ContentRouteValue>(
    () => ({ programId, programSlug, subSpaceId, subSpaceSlug, lessonId, lessonSlug }),
    [programId, programSlug, subSpaceId, subSpaceSlug, lessonId, lessonSlug],
  );
  return <ContentRouteContext.Provider value={memoized}>{children}</ContentRouteContext.Provider>;
};

/**
 * Read the resolved content route. Throws if used outside a slug route so a
 * missing provider surfaces immediately rather than silently building broken
 * links.
 */
export const useContentRoute = (): ContentRouteValue => {
  const ctx = useContext(ContentRouteContext);
  if (!ctx) {
    throw new Error('useContentRoute must be used within a ContentRouteProvider (slug-based content route)');
  }
  return ctx;
};

/** Non-throwing variant for components that may render outside a content route. */
export const useContentRouteOptional = (): ContentRouteValue | null => useContext(ContentRouteContext);
